import { Sentry } from "@/lib/sentry";
import { logger } from "@/lib/logger";
import { AlertTriangle } from "lucide-react";

/**
 * Fallback UI rendered when an unhandled error is caught.
 * Uses Sentry.ErrorBoundary under the hood for automatic
 * component tree + context capture.
 */
function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[hsl(var(--muted))] via-[hsl(var(--background))] to-[hsl(var(--muted))] p-4">
      <div className="bg-white/60 backdrop-blur-xl rounded-3xl p-10 max-w-md w-full text-center shadow-lg border border-white/40">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-8 h-8 text-red-500" strokeWidth={2} />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          Une erreur est survenue
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          L'application a rencontré un problème inattendu. L'erreur a été enregistrée automatiquement.
        </p>
        <button
          onClick={resetError}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          Recharger la page
        </button>
      </div>
    </div>
  );
}

/**
 * App-level error boundary using Sentry's official component.
 * Automatically captures React component tree, tags, and breadcrumbs.
 */
export function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} resetError={resetError} />
      )}
      beforeCapture={(scope) => {
        scope.setTag("boundary", "root");
      }}
      onError={(error, componentStack) => {
        const err = error instanceof Error ? error : new Error(String(error));
        logger.error(err.message, {
          stack: err.stack,
          metadata: {
            componentStack,
            source: "ErrorBoundary",
          },
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
